import React, { useEffect, useState } from 'react';
import { receptionStatsAPI, sitesAPI, suppliersAPI } from '@/lib/api';
import { formatDate, formatCurrency, supplierShortcutLabel } from '@/lib/utils';
import { toast } from 'sonner';
import { 
    TrendingUp, 
    Package, 
    Calendar,
    Building2,
    BarChart3,
    Search,
    X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import ProductReceptionsDrawer from '@/components/ProductReceptionsDrawer';
import useDrawerRestore from '@/hooks/useDrawerRestore';

export default function ReceptionStatsPage() {
    const [stats, setStats] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sites, setSites] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [earliestReceptionDate, setEarliestReceptionDate] = useState(null);
    const [drawerTarget, setDrawerTarget] = useState(null);
    useDrawerRestore(setDrawerTarget);
    const [filters, setFilters] = useState({
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        siteId: 'all',
        supplierId: 'all'
    });
    // Company shortcut filter (client-side resolution to a list of site_ids).
    const [companyFilter, setCompanyFilter] = useState('all');
    // Editable supplier shortcuts (persisted)
    const STATS_DEFAULT_SUPPLIER_SHORTCUTS = ['Vodelys', 'MG Store', 'Asie Caraibes', 'Le Moulin', 'S.A.P.Y.', 'Global Soucing'];
    const [supplierShortcutNames, setSupplierShortcutNames] = useState(() => {
        try {
            const raw = localStorage.getItem('receptionStats_supplierShortcuts');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) return parsed;
            }
        } catch { /* ignore */ }
        return STATS_DEFAULT_SUPPLIER_SHORTCUTS;
    });
    const [showShortcutEditor, setShowShortcutEditor] = useState(false);
    useEffect(() => {
        try {
            localStorage.setItem('receptionStats_supplierShortcuts', JSON.stringify(supplierShortcutNames));
        } catch { /* ignore */ }
    }, [supplierShortcutNames]);

    const COMPANY_MATCHERS = {
        'Fuji Express': (name) => (name || '').startsWith('FE '),
        'Concept Lab': (name) => name === 'Concept Lab',
        'My Food': (name) => name === 'My Food',
        'Boucherie': (name) => name === 'Boucherie',
    };
    const getSiteIdsForCompany = (companyName) => {
        const matcher = COMPANY_MATCHERS[companyName];
        if (!matcher) return [];
        return sites.filter(s => matcher(s.name)).map(s => s.id);
    };
    const normalizeStr = (s) => (s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
    const resolveShortcutSupplier = (name) => {
        const n = normalizeStr(name);
        if (!n) return null;
        let match = suppliers.find(s => normalizeStr(s.name) === n);
        if (!match) match = suppliers.find(s => normalizeStr(s.name).startsWith(n));
        if (!match) match = suppliers.find(s => normalizeStr(s.name).includes(n));
        return match || null;
    };

    useEffect(() => {
        loadSites();
        loadSuppliers();
        loadEarliestDate();
    }, []);

    useEffect(() => {
        if (filters.startDate && filters.endDate) {
            loadStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, companyFilter]);

    const loadSites = async () => {
        try {
            const response = await sitesAPI.getAll();
            setSites(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des sites');
        }
    };

    const loadSuppliers = async () => {
        try {
            const response = await suppliersAPI.getAll();
            setSuppliers(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des fournisseurs');
        }
    };

    const loadEarliestDate = async () => {
        try {
            const response = await receptionStatsAPI.getEarliestDate();
            if (response.data?.earliest_date) {
                setEarliestReceptionDate(response.data.earliest_date);
            }
        } catch {
            /* silent: month shortcuts will fall back to current year only */
        }
    };

    // Build the list of full-month shortcuts (one per month) from the earliest
    // reception detected up to the current month. Falls back to "current year"
    // when no reception is detected yet.
    const monthShortcuts = React.useMemo(() => {
        const today = new Date();
        let start;
        if (earliestReceptionDate) {
            const [y, m] = earliestReceptionDate.split('-').map(Number);
            start = new Date(y, (m || 1) - 1, 1);
        } else {
            start = new Date(today.getFullYear(), 0, 1);
        }
        const end = new Date(today.getFullYear(), today.getMonth(), 1);
        const out = [];
        const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
        // Hard cap to avoid runaway lists if data is corrupt
        let guard = 0;
        while (cursor <= end && guard < 48) {
            const y = cursor.getFullYear();
            const m = cursor.getMonth();
            const first = new Date(y, m, 1);
            const last = new Date(y, m + 1, 0);
            const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const monthLabels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
            out.push({
                key: `${y}-${m + 1}`,
                label: monthLabels[m],
                year: y,
                start: toIso(first),
                end: toIso(last),
            });
            cursor.setMonth(cursor.getMonth() + 1);
            guard += 1;
        }
        return out;
    }, [earliestReceptionDate]);

    // Group consecutive months by year so the UI can show year-aware labels.
    const monthsByYear = React.useMemo(() => {
        const map = new Map();
        monthShortcuts.forEach((m) => {
            if (!map.has(m.year)) map.set(m.year, []);
            map.get(m.year).push(m);
        });
        return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
    }, [monthShortcuts]);

    const isMonthActive = (m) => filters.startDate === m.start && filters.endDate === m.end;

    const applyMonth = (m) => {
        setFilters({ ...filters, startDate: m.start, endDate: m.end });
    };

    const loadStats = async () => {
        setLoading(true);
        try {
            // If site is specifically picked, ignore company filter for the API
            // (siteId takes precedence). Otherwise expand company → site_ids.
            const siteIdsForCompany = (filters.siteId === 'all' && companyFilter !== 'all')
                ? getSiteIdsForCompany(companyFilter)
                : null;
            const response = await receptionStatsAPI.get(
                filters.startDate,
                filters.endDate,
                filters.siteId === 'all' ? null : filters.siteId,
                filters.supplierId === 'all' ? null : filters.supplierId,
                siteIdsForCompany
            );
            setStats(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des statistiques');
        } finally {
            setLoading(false);
        }
    };

    // Scope actif transmis au drawer des receptions (miroir du filtre stats).
    // - Site specifique -> [siteId]
    // - Entreprise -> tous les sites de l'entreprise
    // - "Tous" -> null (aucun filtre site)
    const drawerScopeSiteIds = React.useMemo(() => {
        if (filters.siteId !== 'all') return [filters.siteId];
        if (companyFilter !== 'all') return getSiteIdsForCompany(companyFilter);
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.siteId, companyFilter, sites]);

    // Quick date range selectors
    const setDateRange = (range) => {
        const end = new Date();
        let start = new Date();
        
        switch (range) {
            case 'week':
                start.setDate(end.getDate() - 7);
                break;
            case 'month':
                start.setMonth(end.getMonth() - 1);
                break;
            case '3months':
                start.setMonth(end.getMonth() - 3);
                break;
            case 'year':
                start.setFullYear(end.getFullYear() - 1);
                break;
            default:
                break;
        }
        
        setFilters({
            ...filters,
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        });
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="reception-stats-page">
            <div>
                <h1 className="text-3xl font-bold text-slate-900">Statistiques Réceptions</h1>
                <p className="text-slate-500 mt-1">Analyse des réceptions de marchandises par période</p>
            </div>

            {/* Shortcut bar (Entreprise / Sites FE / Fournisseurs) */}
            <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg" data-testid="stats-shortcuts">
                {/* Mobile: single-column stack. Desktop (sm+): two-column grid
                    so date pickers/month shortcuts sit next to Entreprise/Sites FE. */}
                <div className="flex flex-col gap-2 sm:grid sm:grid-cols-[auto_minmax(0,1fr)] sm:gap-x-3 sm:gap-y-2 sm:items-center">
                    {/* === Row 1 LEFT: Entreprise shortcuts === */}
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <span className="text-xs font-semibold text-slate-500 mr-1">Entreprise :</span>
                        {['all', ...Object.keys(COMPANY_MATCHERS)].map((company) => {
                            const isActive = companyFilter === company;
                            const label = company === 'all' ? 'Toutes' : company;
                            return (
                                <button
                                    key={company}
                                    type="button"
                                    onClick={() => {
                                        if (filters.siteId !== 'all') setFilters({ ...filters, siteId: 'all' });
                                        setCompanyFilter(company);
                                    }}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                                        isActive
                                            ? 'bg-slate-900 text-white border-slate-900'
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                    }`}
                                    data-testid={`stats-company-shortcut-${company}`}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                    {/* === Row 1 RIGHT: Date pickers + duration shortcuts === */}
                    <div className="flex flex-wrap items-center gap-1 sm:justify-end min-w-0">
                        <Input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                            className="h-8 px-2 text-xs flex-1 min-w-[130px] sm:flex-initial sm:w-[150px]"
                            data-testid="start-date-input"
                        />
                        <span className="text-slate-400 text-xs">→</span>
                        <Input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                            className="h-8 px-2 text-xs flex-1 min-w-[130px] sm:flex-initial sm:w-[150px]"
                            data-testid="end-date-input"
                        />
                        <Button variant="outline" size="sm" className="h-8 text-xs px-2.5" onClick={() => setDateRange('week')}>7j</Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs px-2.5" onClick={() => setDateRange('month')}>1 mois</Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs px-2.5" onClick={() => setDateRange('3months')}>3 mois</Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs px-2.5" onClick={() => setDateRange('year')}>1 an</Button>
                    </div>

                    {/* === Row 2 LEFT: Sites FE shortcuts (no stretch, naturally
                            sized so the row ends near the company shortcuts row) === */}
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <span className="text-xs font-semibold text-slate-500 mr-1 flex-shrink-0">Sites FE :</span>
                        {['Génipa', 'Cluny', 'Dillon', 'Océanis', 'Vauclin', 'Siège'].map((shortName) => {
                            const fullName = `FE ${shortName}`;
                            const site = sites.find(s => s.name === fullName);
                            const isActive = site && filters.siteId === site.id;
                            return (
                                <button
                                    key={shortName}
                                    type="button"
                                    disabled={!site}
                                    onClick={() => {
                                        if (companyFilter !== 'all') setCompanyFilter('all');
                                        setFilters({ ...filters, siteId: isActive ? 'all' : site.id });
                                    }}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border text-center ${
                                        !site
                                            ? 'bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed'
                                            : isActive
                                                ? 'bg-slate-900 text-white border-slate-900'
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                    }`}
                                    data-testid={`stats-site-shortcut-${shortName.toLowerCase()}`}
                                >
                                    {shortName}
                                </button>
                            );
                        })}
                    </div>
                    {/* === Row 2 RIGHT: Full-month shortcuts (grouped by year) === */}
                    <div className="flex flex-wrap items-center gap-1 sm:justify-end min-w-0" data-testid="stats-month-shortcuts">
                        {monthsByYear.map(([year, months], yIdx) => (
                            <React.Fragment key={year}>
                                {yIdx > 0 && <span className="text-[10px] text-slate-300 px-0.5">|</span>}
                                {monthsByYear.length > 1 && (
                                    <span className="text-[10px] font-semibold text-slate-400 mr-0.5">{String(year).slice(-2)}</span>
                                )}
                                {months.map((m) => {
                                    const active = isMonthActive(m);
                                    return (
                                        <button
                                            key={m.key}
                                            type="button"
                                            onClick={() => applyMonth(m)}
                                            className={`h-7 px-2 rounded-md text-[11px] font-semibold border transition-colors ${
                                                active
                                                    ? 'bg-slate-900 text-white border-slate-900'
                                                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                            }`}
                                            data-testid={`stats-month-shortcut-${m.key}`}
                                            title={`${m.start} → ${m.end}`}
                                        >
                                            {m.label}
                                        </button>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* === Row 3: Fournisseurs (spans both columns on desktop) === */}
                    <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
                        <span className="text-xs font-semibold text-slate-500 mr-1 flex-shrink-0">Fournisseurs :</span>
                        {supplierShortcutNames.map((name, idx) => {
                            const supplier = resolveShortcutSupplier(name);
                            const isActive = supplier && filters.supplierId === supplier.id;
                            return (
                                <button
                                    key={`${name}-${idx}`}
                                    type="button"
                                    disabled={!supplier}
                                    onClick={() => supplier && setFilters({ ...filters, supplierId: isActive ? 'all' : supplier.id })}
                                    className={`lg:flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border text-center ${
                                        !supplier
                                            ? 'bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed'
                                            : isActive
                                                ? 'bg-emerald-600 text-white border-emerald-600'
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'
                                    }`}
                                    title={!supplier ? `Fournisseur introuvable : ${name}` : supplier.name}
                                    data-testid={`stats-supplier-shortcut-${normalizeStr(name)}`}
                                >
                                    {supplierShortcutLabel(supplier?.name || name)}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => setShowShortcutEditor(true)}
                            className="px-2.5 py-1.5 rounded-full text-xs font-semibold text-slate-500 border border-dashed border-slate-300 hover:border-emerald-400 hover:text-emerald-600 flex-shrink-0"
                            data-testid="stats-edit-supplier-shortcuts-btn"
                        >
                            Modifier
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters card removed — dates + duration shortcuts moved into the entreprise shortcut row */}

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
                </div>
            ) : stats ? (
                <>
                    {(() => {
                        const cats = [...new Set(stats.by_product.map(p => p.category || 'Sans catégorie'))].sort((a, b) => a.localeCompare(b, 'fr'));
                        const searchNorm = normalizeStr(searchTerm);
                        const matchesSearch = (p) => !searchNorm
                            || normalizeStr(p.product_name).includes(searchNorm)
                            || normalizeStr(p.industrial_product_name).includes(searchNorm)
                            || normalizeStr(p.supplier_name).includes(searchNorm);
                        const visibleProducts = stats.by_product.filter(p =>
                            (!categoryFilter || (p.category || 'Sans catégorie') === categoryFilter) && matchesSearch(p)
                        );
                        return (
                    <>
                    {/* Recherche produit */}
                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                        <Input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Rechercher un produit…"
                            className="h-8 pl-8 pr-7 text-xs rounded-full"
                            data-testid="stats-product-search-input"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                data-testid="stats-product-search-clear"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    {/* Raccourcis catégories */}
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="stats-category-shortcuts">
                        <button
                            type="button"
                            onClick={() => setCategoryFilter(null)}
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${!categoryFilter ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                            data-testid="stats-category-all"
                        >Toutes catégories</button>
                        {cats.map(c => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => setCategoryFilter(prev => prev === c ? null : c)}
                                className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${categoryFilter === c ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'}`}
                                data-testid={`stats-category-${c}`}
                            >{c}</button>
                        ))}
                    </div>
                    {/* Top Products Table */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <CardTitle>Détail par produit{categoryFilter ? ` — ${categoryFilter}` : ''}</CardTitle>
                                <span className="text-sm font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 tabular-nums" data-testid="stats-total-value">
                                    Total : {formatCurrency(visibleProducts.reduce((s, it) => s + (it.total_value || 0), 0))}
                                </span>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            {visibleProducts.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full data-table">
                                        <thead>
                                            <tr className="table-header">
                                                <th className="text-left px-3 py-1">Produit</th>
                                                <th className="text-right px-3 py-1">Quantité totale</th>
                                                <th className="text-right px-3 py-1">Valeur</th>
                                                <th className="text-right px-3 py-1">Nb réceptions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const renderQty = (val, max = 2) => val.toLocaleString('fr-FR', { maximumFractionDigits: max });
                                                const lineEquivalent = (it) => {
                                                    const u = (it.unit || '').toLowerCase().trim();
                                                    if ((it.qty_kg || 0) > 0 && u !== 'kg' && u !== 'g') return `${renderQty(it.qty_kg)} kg`;
                                                    if ((it.qty_l || 0) > 0 && u !== 'l' && u !== 'litre' && u !== 'cl' && u !== 'ml') return `${renderQty(it.qty_l)} L`;
                                                    if ((it.qty_pieces || 0) > 0 && (it.qty_pieces || 0) > (it.total_quantity || 0) + 0.01) return `${renderQty(it.qty_pieces, 0)} pcs`;
                                                    return null;
                                                };

                                                const grouped = {};
                                                visibleProducts.forEach(item => {
                                                    const cat = item.category || 'Sans catégorie';
                                                    if (!grouped[cat]) grouped[cat] = {};
                                                    const fam = item.famille || 'Sans famille';
                                                    if (!grouped[cat][fam]) grouped[cat][fam] = [];
                                                    grouped[cat][fam].push(item);
                                                });
                                                const sortedCats = Object.keys(grouped).sort((a, b) => a.localeCompare(b, 'fr'));
                                                return sortedCats.map(cat => {
                                                    const families = grouped[cat];
                                                    const sortedFams = Object.keys(families).sort((a, b) => a.localeCompare(b, 'fr'));
                                                    return (
                                                        <React.Fragment key={cat}>
                                                            <tr className="bg-slate-100">
                                                                <td colSpan="4" className="px-3 py-1 font-bold text-sm text-slate-600">{cat}</td>
                                                            </tr>
                                                            {sortedFams.map(fam => {
                                                                // Group items inside this family by industrial product
                                                                const ipGroups = {};
                                                                families[fam].forEach(it => {
                                                                    const ipKey = it.industrial_product_id || it.product_id;
                                                                    if (!ipGroups[ipKey]) {
                                                                        ipGroups[ipKey] = {
                                                                            industrial_product_name: it.industrial_product_name,
                                                                            industrial_product_id: it.industrial_product_id,
                                                                            items: [],
                                                                            total_value: 0,
                                                                            total_reception_count: 0,
                                                                            total_qty_kg: 0,
                                                                            total_qty_l: 0,
                                                                            total_qty_pieces: 0,
                                                                        };
                                                                    }
                                                                    ipGroups[ipKey].items.push(it);
                                                                    ipGroups[ipKey].total_value += it.total_value || 0;
                                                                    ipGroups[ipKey].total_reception_count += it.reception_count || 0;
                                                                    ipGroups[ipKey].total_qty_kg += it.qty_kg || 0;
                                                                    ipGroups[ipKey].total_qty_l += it.qty_l || 0;
                                                                    ipGroups[ipKey].total_qty_pieces += it.qty_pieces || 0;
                                                                });
                                                                const sortedIpGroups = Object.values(ipGroups).sort((a, b) => (a.industrial_product_name || '').localeCompare(b.industrial_product_name || '', 'fr'));
                                                                return (
                                                                    <React.Fragment key={`${cat}-${fam}`}>
                                                                        <tr className="bg-slate-50/60">
                                                                            <td colSpan="4" className="pl-6 pr-3 py-0.5 text-xs font-semibold text-slate-400 italic">{fam}</td>
                                                                        </tr>
                                                                        {sortedIpGroups.map((g, gIdx) => {
                                                                            const multi = g.items.length > 1;
                                                                            g.items.sort((a, b) => (b.total_value || 0) - (a.total_value || 0));

                                                                            if (!multi) {
                                                                                const it = g.items[0];
                                                                                return (
                                                                                    <tr key={`${cat}-${fam}-${gIdx}`} className="table-row cursor-pointer hover:bg-purple-50/40" onClick={() => setDrawerTarget({ supplier_product_id: it.product_id, productName: g.industrial_product_name, subtitle: `${it.supplier_name || ''} · ${it.product_name}` })}>
                                                                                        <td className="px-3 py-0.5 pl-12">
                                                                                            <div className="font-semibold text-sm text-slate-700 leading-tight">{g.industrial_product_name}</div>
                                                                                            <div className="grid items-baseline leading-tight pl-4" style={{ gridTemplateColumns: '400px auto' }}>
                                                                                                <span className="text-sm text-slate-400">{it.product_name}</span>
                                                                                                <span className="text-sm text-slate-400 italic whitespace-nowrap">{it.supplier_name || ''}</span>
                                                                                            </div>
                                                                                        </td>
                                                                                        <td className="px-3 py-0.5 text-right align-bottom">
                                                                                            <span className="inline-flex items-baseline gap-1.5 tabular-nums">
                                                                                                <span className="font-semibold text-emerald-600 text-right min-w-[72px]">{it.total_quantity.toFixed(2)}</span>
                                                                                                <span className="text-xs text-slate-400 font-normal text-left min-w-[68px]">{it.unit || ''}</span>
                                                                                                <span className="text-xs text-slate-400 font-normal italic text-left min-w-[110px]">
                                                                                                    {lineEquivalent(it) ? `≈ ${lineEquivalent(it)}` : ''}
                                                                                                </span>
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-3 py-0.5 text-right align-bottom">
                                                                                            <span className="font-medium text-slate-600">{formatCurrency(it.total_value || 0)}</span>
                                                                                        </td>
                                                                                        <td className="px-3 py-0.5 text-right align-bottom">{it.reception_count}</td>
                                                                                    </tr>
                                                                                );
                                                                            }

                                                                            // Multi: header row + one row per supplier product
                                                                            const rows = [
                                                                                <tr key={`${cat}-${fam}-${gIdx}-head`} className="table-row bg-slate-50/40 cursor-pointer hover:bg-purple-50/40" onClick={() => setDrawerTarget({ industrial_product_id: g.industrial_product_id, productName: g.industrial_product_name, subtitle: `${g.items.length} fournisseurs` })}>
                                                                                    <td className="px-3 pt-0.5 pb-0 pl-12">
                                                                                        <div className="font-semibold text-sm text-slate-700 leading-tight">{g.industrial_product_name}</div>
                                                                                    </td>
                                                                                    <td className="px-3 pt-0.5 pb-0 text-right align-bottom">
                                                                                        <span className="inline-flex items-baseline gap-1.5 tabular-nums">
                                                                                            <span className="min-w-[72px]"></span>
                                                                                            <span className="min-w-[68px]"></span>
                                                                                            <span className="text-left min-w-[110px] text-xs leading-tight">
                                                                                                {g.total_qty_kg > 0 && (
                                                                                                    <div><span className="font-bold text-emerald-700">{renderQty(g.total_qty_kg)}</span> <span className="text-slate-400 font-normal">kg</span></div>
                                                                                                )}
                                                                                                {g.total_qty_l > 0 && (
                                                                                                    <div><span className="font-bold text-emerald-700">{renderQty(g.total_qty_l)}</span> <span className="text-slate-400 font-normal">L</span></div>
                                                                                                )}
                                                                                                {g.total_qty_pieces > 0 && (
                                                                                                    <div><span className="font-bold text-emerald-700">{renderQty(g.total_qty_pieces, 0)}</span> <span className="text-slate-400 font-normal">pcs</span></div>
                                                                                                )}
                                                                                            </span>
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="px-3 pt-0.5 pb-0 text-right align-bottom">
                                                                                        <span className="font-bold text-slate-700">{formatCurrency(g.total_value)}</span>
                                                                                    </td>
                                                                                    <td className="px-3 pt-0.5 pb-0 text-right align-bottom text-slate-700 font-semibold">{g.total_reception_count}</td>
                                                                                </tr>
                                                                            ];
                                                                            g.items.forEach((it, iIdx) => {
                                                                                rows.push(
                                                                                    <tr key={`${cat}-${fam}-${gIdx}-${iIdx}`} className="table-row cursor-pointer hover:bg-blue-50/40" onClick={() => setDrawerTarget({ supplier_product_id: it.product_id, productName: g.industrial_product_name, subtitle: `${it.supplier_name || ''} · ${it.product_name}` })}>
                                                                                        <td className="px-3 py-0 pl-12">
                                                                                            <div className="grid items-baseline leading-tight pl-4" style={{ gridTemplateColumns: '400px auto' }}>
                                                                                                <span className="text-sm text-slate-400">{it.product_name}</span>
                                                                                                <span className="text-sm text-slate-400 italic whitespace-nowrap">{it.supplier_name || ''}</span>
                                                                                            </div>
                                                                                        </td>
                                                                                        <td className="px-3 py-0 text-right align-bottom">
                                                                                            <span className="inline-flex items-baseline gap-1.5 tabular-nums">
                                                                                                <span className="font-semibold text-emerald-600 text-right min-w-[72px]">{it.total_quantity.toFixed(2)}</span>
                                                                                                <span className="text-xs text-slate-400 font-normal text-left min-w-[68px]">{it.unit || ''}</span>
                                                                                                <span className="text-xs text-slate-400 font-normal italic text-left min-w-[110px]">
                                                                                                    {lineEquivalent(it) ? `≈ ${lineEquivalent(it)}` : ''}
                                                                                                </span>
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-3 py-0 text-right align-bottom">
                                                                                            <span className="text-slate-300 text-sm">{formatCurrency(it.total_value || 0)}</span>
                                                                                        </td>
                                                                                        <td className="px-3 py-0 text-right align-bottom text-slate-400">{it.reception_count}</td>
                                                                                    </tr>
                                                                                );
                                                                            });
                                                                            return rows;
                                                                        })}
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    );
                                                });
                                            })()}
                                        </tbody>
                                        <tfoot className="bg-slate-50 border-t-2 border-slate-200 sticky bottom-0">
                                            <tr>
                                                <td colSpan={2} className="px-3 py-2 text-right font-semibold text-slate-600">Total valeur :</td>
                                                <td className="px-3 py-2 text-right font-bold text-emerald-700 tabular-nums" data-testid="stats-total-value-footer">
                                                    {formatCurrency(visibleProducts.reduce((s, it) => s + (it.total_value || 0), 0))}
                                                </td>
                                                <td className="px-3 py-2 text-right font-semibold text-slate-600 tabular-nums">
                                                    {visibleProducts.reduce((s, it) => s + (it.reception_count || 0), 0)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            ) : (
                                <div className="py-12 text-center text-slate-400">
                                    <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>Aucune réception pour cette période</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    </>
                        );
                    })()}
                </>
            ) : (
                <Card>
                    <CardContent className="py-12 text-center">
                        <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">Sélectionnez une période pour voir les statistiques</p>
                    </CardContent>
                </Card>
            )}

            <ProductReceptionsDrawer
                open={!!drawerTarget}
                onClose={() => setDrawerTarget(null)}
                industrialProductId={drawerTarget?.industrial_product_id || null}
                supplierProductId={drawerTarget?.supplier_product_id || null}
                productName={drawerTarget?.productName || ''}
                subtitle={drawerTarget?.subtitle || ''}
                initialStartDate={filters.startDate}
                initialEndDate={filters.endDate}
                siteIds={drawerScopeSiteIds}
                supplierId={filters.supplierId === 'all' ? null : filters.supplierId}
            />

            <SupplierShortcutEditor
                open={showShortcutEditor}
                onClose={() => setShowShortcutEditor(false)}
                suppliers={suppliers}
                shortcutNames={supplierShortcutNames}
                onChange={setSupplierShortcutNames}
                defaults={STATS_DEFAULT_SUPPLIER_SHORTCUTS}
                resolveShortcutSupplier={resolveShortcutSupplier}
            />
        </div>
    );
}

/** Compact editor dialog for the user-editable supplier shortcut list (Stats Réceptions). */
function SupplierShortcutEditor({ open, onClose, suppliers, shortcutNames, onChange, defaults, resolveShortcutSupplier }) {
    const [draft, setDraft] = useState([]);
    const [newName, setNewName] = useState('');
    useEffect(() => {
        if (open) {
            setDraft([...shortcutNames]);
            setNewName('');
        }
    }, [open, shortcutNames]);
    const handleAdd = () => {
        const name = newName.trim();
        if (!name) return;
        if (draft.some(n => n.toLowerCase() === name.toLowerCase())) {
            toast.error('Ce fournisseur est déjà dans les raccourcis');
            return;
        }
        setDraft([...draft, name]);
        setNewName('');
    };
    const handleRemove = (idx) => setDraft(draft.filter((_, i) => i !== idx));
    const handleMove = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= draft.length) return;
        const copy = [...draft];
        [copy[idx], copy[target]] = [copy[target], copy[idx]];
        setDraft(copy);
    };
    const handleSave = () => { onChange(draft); onClose(); };
    const handleReset = () => setDraft([...defaults]);
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="stats-shortcut-editor-dialog">
                <DialogHeader>
                    <DialogTitle>Raccourcis fournisseurs (Stats)</DialogTitle>
                    <DialogDescription>
                        Sélectionnez un fournisseur, ou tapez un nom librement.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-72 overflow-auto py-1">
                    {draft.map((name, idx) => {
                        const supplier = resolveShortcutSupplier(name);
                        return (
                            <div key={`${name}-${idx}`} className="flex items-center gap-1 border rounded px-2 py-1">
                                <span className="flex-1 text-sm">
                                    {supplier?.name || name}
                                    {!supplier && <span className="text-xs text-red-500 ml-2">(introuvable)</span>}
                                </span>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => handleMove(idx, -1)} disabled={idx === 0}>↑</Button>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => handleMove(idx, 1)} disabled={idx === draft.length - 1}>↓</Button>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-500" onClick={() => handleRemove(idx)}>×</Button>
                            </div>
                        );
                    })}
                </div>
                <div className="flex gap-1">
                    <Select value="" onValueChange={(v) => v && setNewName(v)}>
                        <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Choisir un fournisseur…" /></SelectTrigger>
                        <SelectContent>
                            {suppliers
                                .filter(s => !draft.some(n => resolveShortcutSupplier(n)?.id === s.id))
                                .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
                                .map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)
                            }
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex gap-1">
                    <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Ou taper un nom..." className="h-8 text-xs" onKeyDown={(e) => e.key === 'Enter' && handleAdd()} />
                    <Button size="sm" onClick={handleAdd} className="h-8 text-xs">Ajouter</Button>
                </div>
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={handleReset}>Réinitialiser</Button>
                    <Button variant="outline" size="sm" onClick={onClose}>Annuler</Button>
                    <Button size="sm" onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600 text-white">Enregistrer</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
